import { useTranslation } from "react-i18next";
import { useLocation, useNavigate, Navigate } from "react-router-dom";
import {
  IBreadcrumbsAndMenu,
  IExporter,
  IMenuOption,
  ISelectOption,
  ITab,
} from "../../types/global";
import CustomizedBreadcrumbs from "../../components/Custom/CustomizedBreadcrumbs";
import { Box, Typography } from "@mui/material";
import CustomizedButton from "../../components/Custom/CustomizedButton";
import CustomizedTab from "../../components/Custom/CustomizedTab";
import ContactTable from "../../components/Table/Contact";
import CustomizedMenuOptionsMeatball from "components/Custom/CustomizedMenuOptionsMeatball";
import { useSnackbar } from "notistack";
import { useState, useEffect } from "react";
import {
  useGenerateContactReportQuery,
  GenerateContactReportQuery,
  ContactReportType,
  ReportContactGenerateInput,
} from "generated/crm";
import { createGraphQLClientWithMiddleware } from "services/graphqlClient";
import { useModal } from "hooks/use-modal";
import ExporterContactModal from "components/UI/Modal/ExporterContactModal";
import LoadingAnimation from "components/Animation/Loading";
import { useStateContext } from "contexts/auth-context";
import { useElementHeight } from "hooks/use-element-height";

type Props = {
  type: "all" | "customer" | "supplier";
};

const Contact = ({ type }: Props) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { enqueueSnackbar } = useSnackbar();
  const {
    state: { permissions },
  } = useStateContext();

  const [headerRef, headerHeight] = useElementHeight();
  const breadcrumbs: IBreadcrumbsAndMenu[] = [
    {
      name: t("contact.index"),
      to: "/contact",
    },
    {
      name: t(`contact.contact_type.${type}`),
    },
  ];

  const canViewAll = permissions?.customer?.view && permissions?.supplier?.view;
  const canViewCustomer = permissions?.customer?.view;
  const canViewSupplier = permissions?.supplier?.view;

  const tabs: ITab[] = [
    canViewAll && {
      label: t("status.all"),
      path: `/contact/all`,
    },
    canViewCustomer && {
      label: t("contact.contact_type.customer"),
      path: `/contact/customer`,
    },
    canViewSupplier && {
      label: t("contact.contact_type.supplier"),
      path: `/contact/supplier`,
    },
  ].filter(Boolean) as ITab[];

  const currentTab = pathname;
  const options: IMenuOption[] = [
    (permissions?.customer?.create || permissions?.supplier?.create) && {
      value: "นำเข้าผู้ติดต่อ",
    },
    (permissions?.customer?.export || permissions?.supplier?.export) && {
      value: "นำออกผู้ติดต่อ",
    },
  ].filter(Boolean) as IMenuOption[];

  const typeOptions: ISelectOption[] = [
    permissions?.customer?.export &&
    permissions?.supplier?.export && {
      label: "ผู้ติดต่อ",
      value: "contact",
    },
    permissions?.customer?.export && {
      label: "ลูกค้า",
      value: "customer",
    },
    permissions?.supplier?.export && {
      label: "ซัพพลายเออร์",
      value: "supplier",
    },
  ].filter(Boolean) as ISelectOption[];

  const { modal, openModalHandler, closeModalHandler } = useModal();

  const graphqlClient = createGraphQLClientWithMiddleware("crm");

  const [exportInput, setExportInput] =
    useState<ReportContactGenerateInput | null>(null);

  const { refetch: refetchData, isLoading: isExporting } =
    useGenerateContactReportQuery<GenerateContactReportQuery>(
      graphqlClient,
      {
        input: exportInput,
      },
      {
        enabled: false,
      }
    );

  const exportHandler = async (data: IExporter) => {
    if (data) {
      const { type, start_date, end_date } = data;
      setExportInput({
        report_type: type as ContactReportType,
        start_date: start_date,
        end_date: end_date,
      });
    }
  };

  useEffect(() => {
    const fetchDataAndOpenWindow = async () => {
      try {
        if (exportInput) {
          const { data: exportData } = await refetchData();
          if (exportData?.GenerateReportContactNoQueue) {
            const url = exportData.GenerateReportContactNoQueue;
            window.open(url, "_blank");
            enqueueSnackbar(`${t("button.export")}สำเร็จ`, {
              variant: "success",
            });
          } else {
            throw new Error("No data received");
          }
        }
      } catch (error) {
        console.error("Error fetching data:", error);
        enqueueSnackbar(`${t("button.export")}ไม่สำเร็จ`, {
          variant: "error",
        });
      }
    };

    if (exportInput !== null) {
      fetchDataAndOpenWindow();
    }
  }, [enqueueSnackbar, exportInput, refetchData, t]);

  if (permissions && !permissions.contact?.view) {
    return <Navigate replace to="/unauthorized" />;
  }

  if (
    permissions &&
    !canViewAll &&
    canViewCustomer &&
    !canViewSupplier &&
    type !== "customer"
  ) {
    return <Navigate replace to="/contact/customer" />;
  }

  if (
    permissions &&
    !canViewAll &&
    !canViewCustomer &&
    canViewSupplier &&
    type !== "supplier"
  ) {
    return <Navigate replace to="/contact/supplier" />;
  }

  if (exportInput && isExporting) {
    return <LoadingAnimation />;
  }
  return (
    <>
      <Box ref={headerRef}>
        <CustomizedBreadcrumbs breadcrumbs={breadcrumbs} />
        <Box display="flex" justifyContent="space-between" mt={2}>
          <Typography variant="h5">{t("contact.index")}</Typography>
          <Box display="flex" alignItems="center">
            <Box ml="auto">
              {(permissions?.customer?.create ||
                permissions?.supplier?.create) && (
                  <CustomizedButton
                    startAddIcon
                    title={`${t("button.create")}${t("contact.index")}`}
                    variant="contained"
                    // onClick={() => navigate(`/contact/all/add?tab=address`)}
                    onClick={() =>
                      navigate(
                        `/contact/all/add?tab=address&from=${type === "supplier" ? "supplier" : "customer"
                        }`
                      )
                    }
                  />
                )}
              {(permissions?.customer?.create ||
                permissions?.supplier?.create ||
                permissions?.customer?.export ||
                permissions?.supplier?.export) && (
                  <CustomizedMenuOptionsMeatball
                    isIcon
                    options={options}
                    onSelect={(e) => {
                      const value = e.target as HTMLElement;
                      switch (value.innerText) {
                        case "นำเข้าผู้ติดต่อ":
                          navigate(`/contact/import`);
                          break;
                        case "นำออกผู้ติดต่อ":
                          openModalHandler();
                          break;
                        default:
                      }
                    }}
                    sx={{
                      width: "44px",
                      minWidth: "44px",
                      height: "33px",
                      minHeight: "33px",
                    }}
                    disableRipple={true}
                  />
                )}
            </Box>
          </Box>
        </Box>
        <CustomizedTab tabs={tabs} currentTab={currentTab} divider table />
      </Box>
      <ContactTable type={type} height={`calc(90vh - ${headerHeight + 85}px)`} />
      {typeOptions?.length && (
        <ExporterContactModal
          open={modal}
          closeModalHandler={closeModalHandler}
          typeOptions={typeOptions}
          exportHandler={exportHandler}
        />
      )}
    </>
  );
};

export default Contact;
